import StatusChip from 'components/@extended/StatusChip';
import PartyName from 'components/party/PartyName';
import { SectionEditButton } from 'components/section-edit/SectionEdit';
import { ReactNode } from 'react';
import { ClassifierData } from 'types/classifiers';
import {
    ClientIdentificationPanelDto,
    PanelGroundsDto,
    PanelPersonDto,
    PanelTypedRowDto,
} from 'types/clientIdentification';
import { t } from 'utils/getTranslations';
import { itemValue, useIdentityValueContext } from '../identityValues';
import { itemLabel } from '../missingItems';
import { Value, VALUE_GRID_CLASS } from './Block';

export const groundsText = (grounds: PanelGroundsDto): string | null =>
    grounds.code
        ? [t(`client-identification.grounds.${grounds.code}`), grounds.code === 'OTHER' ? grounds.note : null]
              .filter(Boolean)
              .join(' · ')
        : null;

function GroundsValue({ grounds }: { grounds: PanelGroundsDto }) {
    return (
        <Value label={t('client-identification.item.GROUNDS')} required missing={grounds.isMissing} wide>
            {groundsText(grounds)}
        </Value>
    );
}

function PersonCard({
    name,
    chips,
    onEdit,
    children,
}: {
    name: ReactNode;
    chips: ReactNode;
    onEdit?: () => void;
    children: ReactNode;
}) {
    return (
        <article className="border-border rounded-[10px] border">
            <div className="flex flex-wrap items-center gap-2 px-3.5 py-2.5">
                <span className="font-semibold">{name}</span>
                {chips}
                <span className="flex-auto" />
                {onEdit ? <SectionEditButton onClick={onEdit} /> : null}
            </div>
            <div className="px-3.5 pb-3.5">{children}</div>
        </article>
    );
}

interface Props {
    panel: ClientIdentificationPanelDto;
    classifiers: Record<string, ClassifierData>;
    onEditPerson?: (person: PanelPersonDto) => void;
    onEditTypedRow?: (row: PanelTypedRowDto) => void;
}

/** A related person's own details are asked, never required, so an empty one is a dash and never missing. */
export function IdentificationBlock({ panel, classifiers, onEditPerson, onEditTypedRow }: Props) {
    const context = useIdentityValueContext(classifiers);
    if (panel.people.length === 0 && panel.typedRows.length === 0) {
        return <p className="text-body1 text-fg-secondary m-0">{t('client-identification.no-cards')}</p>;
    }
    return (
        <div className="flex flex-col gap-2.5">
            {panel.people.map((person) => (
                <PersonCard
                    key={person.partyId}
                    name={<PartyName party={person} isLink renderPartyIcon variant="readonly-other" />}
                    chips={person.roleNames.map((roleName) => (
                        <StatusChip key={roleName} type="neutral" size="small" label={roleName} />
                    ))}
                    onEdit={onEditPerson ? () => onEditPerson(person) : undefined}
                >
                    <div className={VALUE_GRID_CLASS}>
                        {person.itemKeys.map((key) => (
                            <Value key={key} label={itemLabel(key)}>
                                {itemValue(key, person.fields, person.partyType, context)}
                            </Value>
                        ))}
                        {person.grounds ? <GroundsValue grounds={person.grounds} /> : null}
                    </div>
                </PersonCard>
            ))}
            {panel.typedRows.map((row) => (
                <PersonCard
                    key={row.rowId}
                    name={row.name}
                    chips={
                        <>
                            <StatusChip type="neutral" size="small" label={t('client-identification.text-mark')} />
                            <StatusChip type="neutral" size="small" label={row.roleName} />
                        </>
                    }
                    onEdit={onEditTypedRow ? () => onEditTypedRow(row) : undefined}
                >
                    <div className={VALUE_GRID_CLASS}>
                        <GroundsValue grounds={row.grounds} />
                    </div>
                </PersonCard>
            ))}
        </div>
    );
}
